import React, { useState, useEffect } from "react";
import { array, func, number, bool, string } from "prop-types";

import { MemberCard } from "../member-card";
import { MemberDirectorySearch } from "../member-directory-search";
import { RegionFilter } from "../region-filter";
import { FilterPills } from "../filter-pills";
import { Pagination } from "../../atoms/Pagination";
import "./member-directory.m.css";
import { MEMBER_DIRECTORY_CONTENT } from "../../../constants";

export const MemberDirectory = ({
  members = [],
  onSearch,
  onFilter,
  itemsPerPage = 10,
  isLoading = false,
  error = null,
  onRetry,
  availableRegions = [],
  selectedRegions = [],
  onRegionFilter,
  onRemoveRegionFilter,
  onClearAllFilters,
  primaryRole,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [openContactId, setOpenContactId] = useState(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const totalPages = Math.ceil(members.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMembers = members.slice(startIndex, endIndex);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setCurrentPage(1);
    if (onSearch) {
      onSearch("");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewAllMembers = () => {
    setSearchTerm("");
    setCurrentPage(1);
    if (onSearch) {
      onSearch("");
    }
  };

  const handleContactToggle = (contactId) => {
    setOpenContactId(contactId);
  };

  // Check if we have search results
  const hasSearchTerm = searchTerm.trim().length > 0;
  const hasSearchResults = hasSearchTerm && currentMembers.length > 0;
  const isNoSearchResults = hasSearchTerm && currentMembers.length === 0 && !isLoading;

  return (
    <div styleName="member-directory" {...props}>
      <MemberDirectorySearch
        value={searchTerm}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        disabled={isLoading}
      />
      {/* Only show filters when we have results or no search term */}
      {!isNoSearchResults && (
        <>
          <div styleName="filters-section">
            <RegionFilter
              availableRegions={availableRegions}
              selectedRegions={selectedRegions}
              onRegionSelect={onRegionFilter}
              onRegionRemove={onRemoveRegionFilter}
              onClearAll={onClearAllFilters}
              onPageReset={setCurrentPage}
              isMobile={isMobile}
            />
          </div>

          {!isMobile && (
            <FilterPills
              selectedRegions={selectedRegions}
              onRemoveFilter={(region) => {
                onRemoveRegionFilter(region);
                setCurrentPage(1);
              }}
            />
          )}
        </>
      )}

      {/* Loading State */}
      {isLoading && (
        <div styleName="loading-container">
          {MEMBER_DIRECTORY_CONTENT.LOADING_TEXT}
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div styleName="error-container">
          {MEMBER_DIRECTORY_CONTENT.ERROR_PREFIX} {error}
          {onRetry && (
            <button
              onClick={onRetry}
              styleName="error-retry-button"
            >
              {MEMBER_DIRECTORY_CONTENT.RETRY_BUTTON_TEXT}
            </button>
          )}
        </div>
      )}

      {/* No Search Results */}
      {isNoSearchResults && !error && (
        <div styleName="no-results-container">
          <p styleName="no-results-text">
            {MEMBER_DIRECTORY_CONTENT.NO_RESULTS_PREFIX} “{searchTerm}”, {MEMBER_DIRECTORY_CONTENT.NO_RESULTS_SUFFIX}
          </p>
          <button
            onClick={handleViewAllMembers}
            styleName="view-all-button"
          >
            <span styleName="view-all-text">
              {MEMBER_DIRECTORY_CONTENT.VIEW_ALL_MEMBERS}
            </span>
          </button>
        </div>
      )}

      {/* Content - only show when not loading, no error, and either has search results or no search term */}
      {!isLoading && !error && !isNoSearchResults && (
        <>
          <div styleName="directory-content">
            <div styleName="section-header">
              <h2 styleName="results-title">{members.length} {primaryRole} {MEMBER_DIRECTORY_CONTENT.RESULTS_TITLE_SUFFIX}</h2>
            </div>

            <div styleName="members-grid">
              {Array.from({ length: Math.ceil(currentMembers.length / 2) }, (_, rowIndex) => (
                <div key={rowIndex} styleName="members-row">
                  {currentMembers.slice(rowIndex * 2, rowIndex * 2 + 2).map((member) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      openContactId={openContactId}
                      onContactToggle={handleContactToggle}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div styleName="pagination-section" style={{marginBottom: '120px'}}>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

MemberDirectory.propTypes = {
  members: array,
  onSearch: func,
  onFilter: func,
  itemsPerPage: number,
  isLoading: bool,
  error: string,
  onRetry: func,
  availableRegions: array,
  selectedRegions: array,
  onRegionFilter: func,
  onRemoveRegionFilter: func,
  onClearAllFilters: func
};